/**
 * InspectLayer — "tap a module, don't use it".
 *
 * A transparent layer over a rendered widget that turns every tap into a
 * SELECTION: it swallows the event (so no host action fires) and reports the
 * blueprint id of whatever was hit. The ids come straight from the DOM —
 * `elementsFromPoint(...)` then the nearest `[data-vi-node]` marker the
 * interpreter leaves on every node when `inspect` is set — so it needs no
 * per-component bookkeeping and works for any blueprint.
 *
 * With `focus` set it cycles: modules NEST and OVERLAP, so one point covers
 * several (a tile inside a grid inside a page; a capture button over a grid). Tapping the
 * focused module walks OUT to the next module under the point, so repeated taps
 * traverse the stack; past the outermost it releases (`onEmpty`). A tap on a
 * DIFFERENT module jumps focus straight to it. Without `focus`, a tap just
 * picks the innermost module (or `onEmpty` on a miss) — jump, no cycle.
 *
 * WHICH of the stacked ids that is, is a blueprint question, not a DOM one:
 * `tapTarget` resolves the innermost MEANINGFUL node — skipping pure layout
 * containers and collapsing a card template's leaves onto the card — so a clip
 * tap lands on the `clipRail` collection instead of the anonymous `Stack` two levels
 * inside its template. Without a `blueprint` no type is knowable, so nothing
 * can be skipped and the innermost candidate wins: that is the connection
 * cards, which only reorder and never read the id.
 *
 * The hover outline is a stylesheet rule keyed by the hovered id, because the
 * marker is `display: contents` and paints nothing itself; the rule reaches
 * its children. It is deliberately GLOBAL: hovering a module in the preview
 * also outlines the same module in the graph below, which is the point.
 *
 * Render it AFTER any host chrome that must stay clickable (the '+' capture button, the
 * back chevron): both are absolutely positioned, so DOM order decides who
 * receives the tap. On the studio's diagram cards nothing is exempt — the
 * layer covers the whole card so no tap can reach the widget.
 */
import { useCallback, useState } from 'react';
import { tapTarget } from './focusModel';

const ACCENT = '#6E59F2';

export default function InspectLayer({
  onPick, onEmpty = null, focus = null, blueprint = null, testId = 'inspect-overlay',
}) {
  const [hover, setHover] = useState(null);

  // Ordered blueprint ids under the point, leaf → root, deduped.
  // `elementsFromPoint` is top-of-stack first, so the nearest marker of each
  // stacked element already reads inner → outer.
  const nodesAt = useCallback((e) => {
    const stack = document.elementsFromPoint?.(e.clientX, e.clientY) || [];
    const ids = [];
    for (const el of stack) {
      const id = el.closest?.('[data-vi-node]')?.getAttribute('data-vi-node');
      if (id && !ids.includes(id)) ids.push(id);
    }
    return ids;
  }, []);

  const id = hover && (globalThis.CSS?.escape ? globalThis.CSS.escape(hover) : hover);

  return (
    <>
      {id ? (
        <style>{`[data-vi-node="${id}"] > * { outline: 2px dashed ${ACCENT}; outline-offset: 1px; border-radius: 6px; }`}</style>
      ) : null}
      <div
        data-testid={testId}
        title={hover || 'tap a module to focus it'}
        // Wheel is handled by the phone FRAME (PhoneCard), an ancestor of this
        // overlay: it scrolls the app's own scroller under the pointer and
        // absorbs the scroll so it never chains to the studio page.
        // the outline previews the TAP, so it draws the module a click would
        // focus (`focus: null` — the jump reading), never the raw leaf under
        // the cursor that the tap is about to resolve away
        onMouseMove={(e) => setHover(tapTarget(blueprint, nodesAt(e)))}
        onMouseLeave={() => setHover(null)}
        // A tap SELECTS; it NEVER reaches the app underneath — these cards are
        // diagrams, not the running widget. See the header for the focus-walk.
        onClick={(e) => {
          const next = tapTarget(blueprint, nodesAt(e), focus);
          // null = nothing under the point, or nothing meaningful left to walk
          // out to — both mean "let go", which is what `onEmpty` is for
          if (next) onPick?.(next); else onEmpty?.();
        }}
        // ABOVE the widget's own stacking. A control inside the page carries its
        // own z-index — a pill `ModeSwitch`'s sticky bar is 2, a `Sheet` 40 —
        // and would otherwise paint over this transparent overlay and keep taking
        // taps. In the focus zone every tap must PICK a module, never drive the
        // app, so the layer sits over all of it. `elementsFromPoint` still reads
        // the markers underneath (it ranks by hit-testability, not z-order).
        style={{ position: 'absolute', inset: 0, zIndex: 60, cursor: 'crosshair' }}
      />
    </>
  );
}
